import React, { useCallback, useMemo, useState } from "react";
import type { ComponentProps, PartData } from "@enonic/react-components";

import SearchResults from "/react4xp/components/shared/SearchResults/SearchResults";
import Pagination from "/react4xp/components/shared/Pagination2/Pagination2";
import axios from "axios";

import type { SearchPartProps } from "./Search.d";

type SearchItem = {
  id?: string;
  title?: string;
  description?: string;
  url?: string;
  image?: string;
  category?: string[];
};

export const SearchPart = (props: ComponentProps<PartData>) => {
  const {
    searchList = [],
    searchQuery = "",
    categoryList: searchCategories = [],
    urlHome = "/",
    limitPage = 10,
  } = (props.data as SearchPartProps) ?? {};

  const [data, setData] = useState(searchList);
  const [dataFromApi, setDataFromApi] = useState(searchQuery !== "");
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = Number(limitPage) || 10;
  const baseHome = urlHome === "/" ? "" : urlHome;

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const handleCategoryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const category = e.target.value;
      const idx = selectedCategories.indexOf(category); // no .includes
      if (idx !== -1) {
        const next = selectedCategories.filter((c) => c !== category);
        setSelectedCategories(next);
      } else {
        setSelectedCategories([...selectedCategories, category]);
      }
      setCurrentPage(1);
    },
    [selectedCategories],
  );

  const handlePaginationChange = useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
  }, []);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      axios
        .get(`${baseHome}/searchFilter?q=${encodeURIComponent(searchTerm)}`)
        .then((response) => {
          setData(response.data || []);
          setDataFromApi(true);
          setCurrentPage(1);
        })
        .catch((error) => console.error(error));
    },
    [baseHome, searchTerm],
  );

  const filteredData = useMemo(() => {
    const term = (searchTerm || "").toLowerCase();
    const list = (data ?? []) as ReadonlyArray<SearchItem>;

    return list.filter((item) => {
      const title = String(item.title ?? "");
      const description = String(item.description ?? "");

      // When data came from the API, the server already ran ngram search across
      // all fields — don't re-filter by term client-side (would drop valid hits
      // whose match field is not title or description).
      const matchesTerm =
        dataFromApi ||
        !term ||
        title.toLowerCase().indexOf(term) !== -1 ||
        description.toLowerCase().indexOf(term) !== -1;

      if (!matchesTerm) return false;
      if (!selectedCategories.length) return true;

      const cats = Array.isArray(item.category)
        ? (item.category as string[])
        : item.category
          ? [String(item.category)]
          : [];

      for (let i = 0; i < selectedCategories.length; i++) {
        if (cats.indexOf(selectedCategories[i]) !== -1) return true;
      }
      return false;
    });
  }, [data, dataFromApi, searchTerm, selectedCategories]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredData.slice(start, end);
  }, [filteredData, currentPage, ITEMS_PER_PAGE]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const from = filteredData.length ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const to = Math.min(currentPage * ITEMS_PER_PAGE, filteredData.length);

  return (
    <section>
      <div className="narrow-container">
        <form className="search-form" onSubmit={handleSearchSubmit}>
          <div className="form-control search-field">
            <label className="visually-hidden" htmlFor="search-input">
              Søk
            </label>
            <div>
              <input
                id="search-input"
                type="text"
                name="q"
                placeholder="Søk"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            <button type="submit" className="btn">
              Søk
            </button>
            <button
              type="button"
              className="btn color-grey"
              onClick={() => {
                setSearchTerm("");
                setData(searchList);
                setDataFromApi(false);
                setSelectedCategories([]);
                setCurrentPage(1);
              }}
            >
              Nullstill
            </button>
          </div>

          {searchCategories && searchCategories.length > 0 && (
            <fieldset>
              <legend>Category</legend>
              {searchCategories.map(({ label, value }, idx) => {
                const checked =
                  selectedCategories.indexOf(String(value)) !== -1; // no .includes
                return (
                  <div key={idx} className="form-control">
                    <input
                      id={String(value)}
                      type="checkbox"
                      name="category"
                      value={String(value)}
                      checked={checked}
                      onChange={handleCategoryChange}
                    />
                    <label htmlFor={String(value)}>{label}</label>
                  </div>
                );
              })}
            </fieldset>
          )}
        </form>

        <SearchResults
          items={paginatedData}
          count={filteredData.length}
          from={from}
          to={to}
          isLoading={false}
        />

        {totalPages > 1 && (
          <Pagination
            pages={totalPages}
            current={currentPage}
            limit={5}
            onPageChange={handlePaginationChange}
          />
        )}
      </div>
    </section>
  );
};

export default SearchPart;
